import type { TimeZoneRange } from "../TimeZoneRange";
import type TimeZoneDefinitionType from "owa-service/lib/contract/TimeZoneDefinitionType";
import type Transition from "owa-service/lib/contract/Transition";
import { MIN_JAVASCRIPT_TIMESTAMP, MAX_JAVASCRIPT_TIMESTAMP, MILLISECONDS_IN_MINUTE, } from "owa-date-constants";

const MS_PER_DAY = 86400000;

/**
 * Category of strict-validation failure that made {@link expandTimeZoneDefinition} decline a
 * definition. Surfaced (opt-in) via {@link TimeZoneExpansionDiagnostics} so callers can emit
 * telemetry counting WHICH definition shapes force a series to server expansion, rather than a
 * single opaque "unexpandable" bucket. Coarse-grained on purpose (one per validation phase):
 * - `invalid_periods`           missing/empty Periods, a Period with a bad Bias / missing Id, or a
 *                               duplicate Period Id.
 * - `invalid_group_structure`   the TransitionsGroups/Transitions timeline is malformed (group
 *                               without id, duplicate group id, switch target not Kind==='Group' or
 *                               referencing an unknown group, unparseable/non-increasing switch
 *                               instant, or no base transition).
 * - `malformed_transition`      a group's transition list is invalid (target not Kind==='Period'
 *                               or an unknown period, recurring fields without a Month, a fixed-date
 *                               (Day) rule also carrying DayOfWeek/Occurrence, out-of-range
 *                               Month/Day/Occurrence, missing/unparseable TimeOffset, or other than
 *                               0/2 recurring rules).
 * - `ambiguous_constant_offset` a no-DST group carries two DIFFERENT constant target offsets, so its
 *                               authoritative offset would be order-dependent — failed closed.
 * - `offset_lookup_failure`     an instant could not be resolved to an offset (a fixed-day rule
 *                               invalid for a year, or no segment covers the instant).
 */
export type TimeZoneExpansionFailureCause = "invalid_periods" | "invalid_group_structure" | "malformed_transition" | "ambiguous_constant_offset" | "offset_lookup_failure";

/**
 * Optional out-parameter for {@link expandTimeZoneDefinition}: when a definition is declined, its
 * `cause` is set to the first failure category encountered. Left untouched on success. Passing it
 * is opt-in — existing callers that ignore diagnostics see identical behavior.
 */
export interface TimeZoneExpansionDiagnostics {
    cause?: TimeZoneExpansionFailureCause;
}

const DAY_OF_WEEK: {
    [name: string]: number;
} = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};

/**
 * Structural view over the heterogeneous `Transition[]` union the wire uses. The generated
 * contract only exposes `To` on the base `Transition`; the concrete variants (AbsoluteDateTransition,
 * RecurringDayTransition, RecurringDateTransition) add the members below. We read them through this
 * optional-field shape and detect the variant by presence rather than `__type`.
 */
interface AnyTransition extends Transition {
    DateTime?: string;
    TimeOffset?: string;
    Month?: number;
    DayOfWeek?: string;
    Occurrence?: number;
    Day?: number;
}

interface TransitionPoint {
    utcMs: number;
    offset: number;
}

/**
 * One recurring DST transition within a group, fully validated. `targetOffset` is the UTC offset
 * (minutes) the clock moves TO when this transition fires; the instant it fires in a given year is
 * computed via {@link ruleTransitionUtc} (which subtracts the offset active BEFORE it — i.e. the
 * OTHER rule's target — to turn the local wall-clock spec into a UTC instant).
 */
interface RecurringRule {
    month: number; // 1..12
    /** Fixed calendar day (RecurringDateTransition). Mutually exclusive with dayOfWeek/occurrence. */
    day?: number;
    /** 0=Sunday..6=Saturday (RecurringDayTransition). */
    dayOfWeek?: number;
    /** 1..4 = nth weekday; 5 or -1 = last matching weekday. */
    occurrence?: number;
    /** Local time-of-day (minutes past midnight) the transition fires. */
    timeOffsetMin: number;
    targetOffset: number;
}

/**
 * A parsed transitions-group. A DST group carries exactly two {@link RecurringRule}s (to-daylight,
 * to-standard); a constant (no-DST) group carries none and a single {@link constantOffset}.
 */
interface ParsedGroup {
    rules: RecurringRule[];
    constantOffset?: number;
}

/**
 * A group with the precise UTC interval `[effectiveStart, effectiveEnd)` over which it is active.
 * This mirrors Substrate's `ExTimeZoneRuleGroup.EffectiveUtcStart/EffectiveUtcEnd`: the group that
 * governs an instant is the one whose interval contains it — so a mid-year rule change (e.g. a
 * jurisdiction dropping DST on a specific date) is honored to the instant, not quantized to Jan 1.
 */
interface GroupSegment extends ParsedGroup {
    effectiveStart: number;
    effectiveEnd: number;
}

/**
 * Expand a wire `TimeZoneDefinitionType` (a raw Windows DST rule) into the `TimeZoneRange[]` the
 * datetime store consumes, covering `[minYear, maxYear]`.
 *
 * This is the client-side equivalent of the server's `ExTimeZone` / `OffsetRanges`: it selects the
 * active transitions-group by UTC instant (Substrate's `GetRuleForUtcTime` model) and resolves the
 * standard/daylight offset within that group, producing the exact UTC transition instants for the
 * series' authoring zone — so DST-boundary occurrences land on the same instant (and the same
 * occurrence-id date-tick) the server would compute, instead of the promoted named zone whose
 * transition dates can differ.
 *
 * STRICT: returns `undefined` for ANY unrecognized / unparseable / out-of-range shape (NaN switch
 * instant, unknown group or period reference, missing TimeOffset, out-of-range Occurrence/Day/Month,
 * or a group with other than 0 or 2 recurring rules). It never fabricates a plausible-but-wrong
 * range — the caller must route an `undefined` to server expansion, never silently fall back to the
 * promoted zone. Uses only epoch arithmetic (`Date.UTC` + integer day math) — no `Date` instances
 * and no wall-clock/`now` dependence.
 *
 * @param definition The wire timezone definition (typically `RecurrenceStartTimeZoneDefinition`).
 * @param minYear First calendar year the ranges must cover.
 * @param maxYear Last calendar year the ranges must cover (inclusive).
 * @param diagnostics Optional out-parameter; on failure its `cause` is set to the failure category.
 * @returns Chronological `TimeZoneRange[]`, or `undefined` if the definition cannot be expanded.
 */
export default function expandTimeZoneDefinition(definition: TimeZoneDefinitionType | undefined, minYear: number, maxYear: number, diagnostics?: TimeZoneExpansionDiagnostics): TimeZoneRange[] | undefined {
    // Record the first failure category (if a diagnostics sink was supplied) and decline.
    const fail = (cause: TimeZoneExpansionFailureCause): undefined => {
        if (diagnostics && diagnostics.cause === undefined) {
            diagnostics.cause = cause;
        }
        return undefined;
    };
    
    if (!definition || !definition.Periods || definition.Periods.length === 0) {
        return fail("invalid_periods");
    }
    if (maxYear < minYear) {
        return undefined;
    }
    
    // Period id -> UTC offset in minutes. Windows convention: offset = -Bias (PT8H => -480).
    const periodOffset = new Map<string, number>();
    for (const period of definition.Periods) {
        const bias = parseDurationMinutes(period.Bias);
        if (bias === undefined || !period.Id) {
            return fail("invalid_periods");
        }
        if (periodOffset.has(period.Id)) {
            // strict: a duplicate period id would silently overwrite an earlier offset, making the
            // resolved offset depend on payload order. A valid definition keys periods by unique id.
            return fail("invalid_periods");
        }
        periodOffset.set(period.Id, -bias);
    }
    if (periodOffset.size === 0) {
        return fail("invalid_periods");
    }
    
    const hasGroups = !!definition.TransitionsGroups && definition.TransitionsGroups.length > 0;
    
    // Raw (unvalidated) transition lists keyed by group id.
    const rawGroups = new Map<string, AnyTransition[]>();
    if (hasGroups) {
        for (const group of definition.TransitionsGroups || []) {
            if (!group.Id) {
                return fail("invalid_group_structure"); // strict: a group without a (non-empty) id can't be referenced.
            }
            if (rawGroups.has(group.Id)) {
                // strict: a duplicate group id would silently overwrite an earlier transition list,
                // making the active group depend on payload order. Ids must be unique keys.
                return fail("invalid_group_structure");
            }
            rawGroups.set(group.Id, (group.Transition || []) as AnyTransition[]);
        }
    }
    
    const topList = (definition.Transitions?.Transition || []) as AnyTransition[];
    
    // Build the group timeline: which group is active from a given UTC instant. The base transition
    // (no DateTime) applies from -infinity; an AbsoluteDateTransition.DateTime switches the active
    // group at that exact instant. When there are no groups this is the FLAT form: the top-level
    // Transition[] IS the single implicit group's recurring-rule list.
    let timeline: {
        from: number;
        groupId: string;
    }[];
    if (hasGroups) {
        timeline = [];
        for (const transition of topList) {
            const groupId = transition.To?.Value;
            if (transition.To?.Kind !== "Group" ||
                groupId === undefined ||
                !rawGroups.has(groupId)) {
                // strict: a top-level switch must target an existing group by Kind==='Group'. A
                // Period-kinded (or kind-less) target here is a malformed timeline we won't guess at.
                return fail("invalid_group_structure");
            }
            let from: number;
            if (transition.DateTime === undefined) {
                from = -Infinity; // base transition (applies from the beginning of time).
            }
            else {
                from = parseWireInstantUtc(transition.DateTime);
                if (Number.isNaN(from)) {
                    return fail("invalid_group_structure"); // strict: unparseable switch instant.
                }
            }
            timeline.push({ from, groupId });
        }
        if (timeline.length === 0) {
            return fail("invalid_group_structure");
        }
        timeline.sort((a, b) => a.from - b.from);
        // Strict: the timeline must cover every instant with exactly one active group. That requires
        // exactly one base transition (applies from -infinity) sorted first, and strictly-increasing
        // switch instants after it. A missing base leaves an uncovered gap before the first switch;
        // duplicate/equal instants make coverage ambiguous (zero-length or overlapping segments).
        if (timeline[0].from !== -Infinity) {
            return fail("invalid_group_structure"); // no base group — pre-first-switch instants uncovered.
        }
        for (let i = 1; i < timeline.length; i++) {
            if (timeline[i].from === -Infinity || timeline[i].from <= timeline[i - 1].from) {
                return fail("invalid_group_structure"); // duplicate base or non-increasing switch instant.
            }
        }
    }
    else {
        rawGroups.set("", topList);
        timeline = [{ from: -Infinity, groupId: "" }];
    }
    
    // Validate + parse every referenced group. Any unparseable group declines the whole definition.
    const parsedGroups = new Map<string, ParsedGroup>();
    for (const [groupId, transitions] of rawGroups) {
        const parsed = parseGroup(transitions, periodOffset, diagnostics);
        if (!parsed) {
            // parseGroup already recorded the specific cause (malformed_transition /
            // ambiguous_constant_offset); default the bucket if it somehow didn't.
            return fail("malformed_transition");
        }
        parsedGroups.set(groupId, parsed);
    }
    
    // Assign each timeline entry its effective UTC interval [effectiveStart, effectiveEnd).
    const segments: GroupSegment[] = [];
    for (let i = 0; i < timeline.length; i++) {
        const parsed = parsedGroups.get(timeline[i].groupId);
        if (!parsed) {
            return fail("invalid_group_structure"); // defensive: timeline referenced an unparsed group.
        }
        segments.push({
            effectiveStart: timeline[i].from,
            effectiveEnd: i + 1 < timeline.length ? timeline[i + 1].from : Infinity,
            rules: parsed.rules,
            constantOffset: parsed.constantOffset,
        });
    }
    
    const spanStartUtc = Date.UTC(minYear, 0, 1);
    const spanEndUtc = Date.UTC(maxYear + 1, 0, 1);
    
    // Offset active AT a UTC instant: pick the segment whose interval contains it (last segment with
    // effectiveStart <= instant), then resolve the offset within that group.
    const offsetAtUtc = (utcMs: number): number | undefined => {
        let seg: GroupSegment | undefined;
        for (const candidate of segments) {
            if (candidate.effectiveStart <= utcMs) {
                seg = candidate;
            }
            else {
                break;
            }
        }
        if (!seg) {
            return undefined; // strict: no segment covers this instant — never fabricate coverage.
        }
        return offsetForSegment(seg, utcMs, minYear, maxYear);
    };
    
    // Collect every transition instant within the span: group-switch boundaries plus each active
    // group's own recurring transitions, clipped to that group's effective interval.
    const candidates = new Set<number>([spanStartUtc]);
    for (const seg of segments) {
        const lo = Math.max(seg.effectiveStart, spanStartUtc);
        const hi = Math.min(seg.effectiveEnd, spanEndUtc);
        if (lo >= hi) {
            continue; // this group is not active anywhere in the span.
        }
        if (seg.effectiveStart > spanStartUtc && seg.effectiveStart < spanEndUtc) {
            candidates.add(seg.effectiveStart); // the group-switch boundary itself.
        }
        if (seg.rules.length === 2) {
            for (let year = minYear - 1; year <= maxYear + 1; year++) {
                for (let i = 0; i < 2; i++) {
                    const inst = ruleTransitionUtc(seg.rules[i], seg.rules[1 - i], year);
                    if (inst === undefined) {
                        return fail("offset_lookup_failure"); // strict: a fixed-day rule invalid for this year.
                    }
                    if (inst >= lo && inst < hi) {
                        candidates.add(inst);
                    }
                }
            }
        }
    }
    
    const sorted = Array.from(candidates).sort((a, b) => a - b);
    const points: TransitionPoint[] = [];
    for (const utcMs of sorted) {
        const offset = offsetAtUtc(utcMs);
        if (offset === undefined) {
            return fail("offset_lookup_failure");
        }
        // Collapse consecutive equal offsets so no zero-information range is emitted.
        if (points.length === 0 || points[points.length - 1].offset !== offset) {
            points.push({ utcMs, offset });
        }
    }
    if (points.length === 0) {
        return fail("offset_lookup_failure");
    }
    return buildRanges(points);
}

/**
 * Validate + parse a group's transition list. Returns a {@link ParsedGroup} (0 recurring rules =>
 * constant offset; exactly 2 => a DST group) or `undefined` for any strict-validation failure. On
 * failure records the category into `diagnostics.cause` (malformed_transition, or
 * ambiguous_constant_offset for two conflicting constant targets).
 */
function parseGroup(transitions: AnyTransition[], periodOffset: Map<string, number>, diagnostics?: TimeZoneExpansionDiagnostics): ParsedGroup | undefined {
    const fail = (cause: TimeZoneExpansionFailureCause): undefined => {
        if (diagnostics && diagnostics.cause === undefined) {
            diagnostics.cause = cause;
        }
        return undefined;
    };
    const rules: RecurringRule[] = [];
    const constantTargets: number[] = [];
    
    for (const transition of transitions) {
        const targetValue = transition.To?.Value;
        const targetOffset = targetValue !== undefined ? periodOffset.get(targetValue) : undefined;
        if (transition.To?.Kind !== "Period" || targetOffset === undefined) {
            // strict: a transition inside a group must point at a Period (Kind==='Period') that
            // resolves to a known offset. A Group-kinded (or kind-less) / unknown target is malformed.
            return fail("malformed_transition");
        }
        
        if (transition.Month === undefined) {
            // A constant period pointer (no-DST group) must carry NONE of the recurring-rule
            // fields and no absolute-instant DateTime. If any recurring field OR a DateTime is
            // present the rule is an unsupported/malformed shape (a recurring transition missing
            // only its Month, or an AbsoluteDateTransition whose DateTime we don't implement inside
            // a group) — reject it rather than silently emitting a plausible flat offset that would
            // mis-expand the series.
            if (transition.DateTime !== undefined ||
                transition.DayOfWeek !== undefined ||
                transition.Occurrence !== undefined ||
                transition.Day !== undefined ||
                transition.TimeOffset !== undefined) {
                return fail("malformed_transition"); // recurring/absolute fields without a Month => malformed.
            }
            constantTargets.push(targetOffset);
            continue;
        }
        
        const month = transition.Month;
        if (!Number.isInteger(month) || month < 1 || month > 12) {
            return fail("malformed_transition"); // strict: out-of-range month.
        }
        if (transition.DateTime !== undefined) {
            // strict: a recurring rule (Month present) describes a yearly wall-clock transition and
            // must NOT also carry an absolute-instant DateTime. The two are incompatible shapes; a
            // present DateTime here would be silently dropped, so fail closed rather than guess which
            // the server intended.
            return fail("malformed_transition");
        }
        const timeOffsetMin = parseDurationMinutes(transition.TimeOffset);
        if (timeOffsetMin === undefined) {
            return fail("malformed_transition"); // strict: a recurring rule must carry a parseable TimeOffset.
        }
        
        if (transition.Day !== undefined) {
            // RecurringDateTransition (fixed calendar day). Per-year day validity is checked in
            // ruleTransitionUtc; here we bound it to a plausible 1..31 to reject garbage early.
            // A fixed-date rule is mutually exclusive with the weekday shape: DayOfWeek/Occurrence
            // must be absent, otherwise the transition mixes two incompatible recurring shapes and
            // we can't know which the server intended — fail closed rather than silently ignore them.
            if (transition.DayOfWeek !== undefined || transition.Occurrence !== undefined) {
                return fail("malformed_transition");
            }
            if (!Number.isInteger(transition.Day) || transition.Day < 1 || transition.Day > 31) {
                return fail("malformed_transition");
            }
            rules.push({ month, day: transition.Day, timeOffsetMin, targetOffset });
        }
        else {
            const dayOfWeek = transition.DayOfWeek !== undefined ? DAY_OF_WEEK[transition.DayOfWeek] : undefined;
            if (dayOfWeek === undefined) {
                return fail("malformed_transition"); // strict: missing/unknown DayOfWeek.
            }
            const occurrence = transition.Occurrence;
            if (occurrence === undefined || !isValidOccurrence(occurrence)) {
                return fail("malformed_transition"); // strict: occurrence must be 1..5 or -1 (last).
            }
            rules.push({ month, dayOfWeek, occurrence, timeOffsetMin, targetOffset });
        }
    }
    
    if (rules.length === 0) {
        if (constantTargets.length === 0) {
            return fail("malformed_transition"); // nothing usable in this group.
        }
        // A no-DST group must resolve to a SINGLE authoritative offset. Multiple transitions may
        // point at the same period (redundant but consistent); but two DIFFERENT constant target
        // offsets make the group's offset order-dependent (whichever transition happens to be last
        // wins) — fail closed to server expansion rather than pick an arbitrary offset.
        const distinctOffsets = new Set(constantTargets);
        if (distinctOffsets.size > 1) {
            return fail("ambiguous_constant_offset");
        }
        return { rules: [], constantOffset: constantTargets[0] };
    }
    if (rules.length !== 2) {
        return fail("malformed_transition"); // strict: a DST group has exactly two recurring transitions.
    }
    return { rules };
}

/** Valid Windows transition occurrence: 1..4 (nth weekday), 5 or -1 (last matching weekday). */
function isValidOccurrence(occurrence: number): boolean {
    return (Number.isInteger(occurrence) && ((occurrence >= 1 && occurrence <= 5) || occurrence === -1));
}

/**
 * The UTC instant a recurring rule fires in `year`. The wire spec is a LOCAL wall-clock time; the
 * offset active immediately BEFORE the transition is the OTHER rule's target (a 2-rule DST group
 * strictly alternates), so `utc = local - otherTargetOffset`. Returns `undefined` for a fixed-day
 * rule whose day does not exist in that year (strict).
 */
function ruleTransitionUtc(rule: RecurringRule, other: RecurringRule, year: number): number | undefined {
    let day: number;
    if (rule.day !== undefined) {
        if (rule.day > daysInMonth(year, rule.month)) {
            return undefined; // strict: e.g. "Feb 30", or Feb 29 in a common year.
        }
        day = rule.day;
    }
    else {
        day = nthWeekdayOfMonth(year, rule.month, rule.dayOfWeek as number, rule.occurrence as number);
    }
    const localMs = Date.UTC(year, rule.month - 1, day) + rule.timeOffsetMin * MILLISECONDS_IN_MINUTE;
    return localMs - other.targetOffset * MILLISECONDS_IN_MINUTE;
}

/**
 * Offset (minutes) active at `utcMs` within one group. A constant group returns its flat offset; a
 * DST group returns the target of the most-recent transition at or before `utcMs` (searching the
 * span's years plus a one-year margin so southern-hemisphere January — governed by the prior year's
 * spring-forward — resolves correctly). Non-sequential: no warm-up / accumulator needed.
 */
function offsetForSegment(seg: GroupSegment, utcMs: number, minYear: number, maxYear: number): number | undefined {
    if (seg.rules.length === 0) {
        return seg.constantOffset;
    }
    let bestInst = -Infinity;
    let bestOffset: number | undefined;
    for (let year = minYear - 1; year <= maxYear + 1; year++) {
        for (let i = 0; i < 2; i++) {
            const inst = ruleTransitionUtc(seg.rules[i], seg.rules[1 - i], year);
            if (inst === undefined) {
                return undefined;
            }
            if (inst <= utcMs && inst > bestInst) {
                bestInst = inst;
                bestOffset = seg.rules[i].targetOffset;
            }
        }
    }
    // Before the earliest transition in the searched window: adopt standard (the smaller/most-
    // negative offset). Unreachable for in-span instants (the prior year always supplies a
    // transition <= utcMs), kept only as a total function.
    if (bestOffset === undefined) {
        bestOffset = Math.min(seg.rules[0].targetOffset, seg.rules[1].targetOffset);
    }
    return bestOffset;
}

/**
 * Chain sorted transition points into `TimeZoneRange[]`. Mirrors `timeZoneRanges` exactly (each
 * range ends where the next starts; `localEnd` uses the larger of the two adjacent offsets; the
 * first range extends back to the beginning of time) but operates on epoch numbers directly to
 * avoid an ISO string round-trip.
 */
function buildRanges(points: TransitionPoint[]): TimeZoneRange[] {
    const ranges: TimeZoneRange[] = points.map(({ utcMs, offset }) => ({
        start: utcMs,
        localStart: utcMs + offset * MILLISECONDS_IN_MINUTE,
        end: MAX_JAVASCRIPT_TIMESTAMP,
        localEnd: MAX_JAVASCRIPT_TIMESTAMP,
        offset,
    }));
    
    for (let i = 0; i < ranges.length - 1; i++) {
        const range = ranges[i];
        const nextRange = ranges[i + 1];
        range.end = nextRange.start;
        range.localEnd =
            range.end + Math.max(range.offset, nextRange.offset) * MILLISECONDS_IN_MINUTE;
    }
    
    ranges[0].start = MIN_JAVASCRIPT_TIMESTAMP;
    ranges[0].localStart = MIN_JAVASCRIPT_TIMESTAMP;
    return ranges;
}

/**
 * Parse a wire `DateTime` (group-switch instant) to a UTC epoch-ms value. The custom-tz wire format
 * is UTC, but a designator-less ISO value (no trailing `Z` and no explicit `±hh:mm` offset) is
 * interpreted by `Date.parse` in the client's LOCAL zone — which would make the parsed switch
 * instant depend on where the code happens to run. Treat any designator-less value as UTC so
 * expansion is deterministic across clients. Returns `NaN` for an unparseable value (callers reject).
 */
function parseWireInstantUtc(dateTime: string): number {
    const hasTzDesignator = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(dateTime);
    return Date.parse(hasTzDesignator ? dateTime : `${dateTime}Z`);
}

/**
 * Parse an ISO-8601 duration (Windows `Bias` / `TimeOffset`, e.g. `PT8H`, `-PT1H`, `PT0H`) to
 * minutes. Returns `undefined` for an absent or unrecognized value.
 */
function parseDurationMinutes(value: string | undefined): number | undefined {
    if (!value) {
        return undefined;
    }
    const match = /^(-)?P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(value);
    if (!match) {
        return undefined;
    }
    // Reject a bare `P` / `PT` carrying no component (all groups absent). A real zero duration is
    // written with an explicit component (e.g. `PT0H`), so an empty duration is a malformed value,
    // not zero — strict validation must not silently treat it as 0.
    if (match[2] === undefined &&
        match[3] === undefined &&
        match[4] === undefined &&
        match[5] === undefined) {
        return undefined;
    }
    const sign = match[1] ? -1 : 1;
    const days = Number(match[2] || 0);
    const hours = Number(match[3] || 0);
    const minutes = Number(match[4] || 0);
    const seconds = Number(match[5] || 0);
    return sign * (days * 1440 + hours * 60 + minutes + seconds / 60);
}

function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month1: number): number {
    if (month1 === 2) {
        return isLeapYear(year) ? 29 : 28;
    }
    return month1 === 4 || month1 === 6 || month1 === 9 || month1 === 11 ? 30 : 31;
}

/** Day-of-week (0=Sunday) for a UTC calendar date, via epoch-day math (no Date instance). */
function dayOfWeekUtc(year: number, month1: number, day: number): number {
    const epochDay = Math.floor(Date.UTC(year, month1 - 1, day) / MS_PER_DAY);
    // 1970-01-01 was a Thursday (4). Extra + 7 keeps the result non-negative for pre-1970 dates.
    return ((((epochDay % 7) + 4) % 7) + 7) % 7;
}

/**
 * Day-of-month for the `occurrence`-th `dayOfWeek` of the month. `occurrence` 1..4 selects the
 * nth weekday; `5` or `-1` selects the LAST matching weekday (Windows encodes "last" as 5).
 */
function nthWeekdayOfMonth(year: number, month1: number, dayOfWeek: number, occurrence: number): number {
    const monthLength = daysInMonth(year, month1);
    if (occurrence === -1 || occurrence >= 5) {
        const lastDow = dayOfWeekUtc(year, month1, monthLength);
        return monthLength - ((lastDow - dayOfWeek + 7) % 7);
    }
    const firstDow = dayOfWeekUtc(year, month1, 1);
    const firstMatch = 1 + ((dayOfWeek - firstDow + 7) % 7);
    const day = firstMatch + (occurrence - 1) * 7;
    return day > monthLength ? day - 7 : day;
}
